import { type LucideIcon, Plus } from "lucide-react";
import type * as React from "react";
import { useState } from "react";
import { DataTable, type DataTableHeader } from "@/components/shared/data-table";
import { DataToolbar } from "@/components/shared/data-toolbar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { FilterRow } from "@/components/shared/filter-bar";
import { FilterPanel, selectedLabels } from "@/components/shared/filter-panel";
import { FilterToggleGroup } from "@/components/shared/filter-toggle-group";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { ViewToggle } from "@/components/shared/view-toggle";
import { Button } from "@/components/ui/button";
import { useCursorList } from "@/hooks/use-cursor-list";
import { useViewMode } from "@/hooks/use-view-mode";
import { useSession } from "@/lib/auth-client";
import { canEditAll } from "@/lib/permissions";

/**
 * The list screen shared by the library entities — skills, competencies and
 * certifications.
 *
 * The three were near-byte-for-byte copies: the same status filter, search
 * box, view toggle, add button, empty state, error state, table/cards switch
 * and pagination, differing in the noun, the icon, and the cells of a row.
 * They had already drifted cosmetically — one said "No certifications" where
 * the others said "No skills found" — which is the usual cost of keeping
 * three copies of one screen.
 *
 * It comes in two halves ON PURPOSE. Each list has to call its OWN data hook,
 * and a hook cannot be handed in as a prop — so `useLibraryList` gives the
 * caller the search/sort/page state at the top level, the caller calls its
 * own hook with it, and `LibraryListView` renders the result.
 *
 * Roles deliberately does NOT use this. Its list carries department and level
 * filters, a vacancy filter deep-linked from Org Health, row selection and a
 * compare mode; parameterising all of that would make this harder to read
 * than the copy it replaced.
 *
 * What a list still owns: its data hook, its columns, its row cells, its
 * cards, and its form dialog.
 */

const LIMIT = 50;

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "archived", label: "Archived" },
];

/** Everything the two halves share, created by `useLibraryList`. */
export type LibraryListState = ReturnType<typeof useLibraryList>;

/**
 * The list's search, sort, status filter, paging and view-mode state.
 *
 * Call this at the top of the list component, pass `query` straight into the
 * entity's data hook, then hand both to `LibraryListView`.
 */
export function useLibraryList(viewModeKey: string) {
  const { data: session } = useSession();
  const cursor = useCursorList({
    sortKey: "name",
    defaultFilters: { statuses: ["active"] as string[] },
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [view, setView] = useViewMode(viewModeKey);
  const statuses = cursor.filters.statuses;

  return {
    ...cursor,
    statuses,
    isAdmin: canEditAll(session?.user?.role),
    createOpen,
    setCreateOpen,
    view,
    setView,
    /** Pass as the filters argument of the entity's list hook. */
    query: {
      search: cursor.debouncedSearch,
      statuses,
      sort: cursor.sort.key,
      dir: cursor.sort.dir,
    },
  };
}

/** The slice of a TanStack Query result the view reads. */
interface LibraryListData<T> {
  data:
    | {
        data: T[];
        nextCursor?: string | null;
        prevCursor?: string | null;
        hasNext?: boolean;
        hasPrev?: boolean;
        total?: number;
      }
    | undefined;
  isLoading: boolean;
  isError: boolean;
  error: { message?: string } | null;
  refetch: () => void;
}

interface LibraryListViewProps<T extends { id: string }> {
  /** Lowercase, for copy: "skill" / "skills". */
  noun: { one: string; many: string };
  icon: LucideIcon;
  list: LibraryListState;
  result: LibraryListData<T>;
  headers: DataTableHeader[];
  /** One `<tr>` per item, including its own click and a11y wiring. */
  renderRow: (item: T) => React.ReactNode;
  renderCards: (args: { items: T[]; loading: boolean; empty: React.ReactNode }) => React.ReactNode;
  /** Admin-only. Omit for a list with no create path. */
  renderCreateDialog?: (args: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
  }) => React.ReactNode;
}

export function LibraryListView<T extends { id: string }>({
  noun,
  icon,
  list,
  result,
  headers,
  renderRow,
  renderCards,
  renderCreateDialog,
}: LibraryListViewProps<T>) {
  const { search, setSearch, sort, onSort, setFilter, resetFilters, pagination, statuses } = list;
  const { data, isLoading, isError, error, refetch } = result;

  const rows = data?.data ?? [];
  const hasActiveFilters = statuses.length > 0 || search.length > 0;
  const filterSummary = statuses.length
    ? [`Status: ${selectedLabels(STATUS_OPTIONS, statuses)}`]
    : [];
  const canCreate = list.isAdmin && renderCreateDialog !== undefined;

  // Reset restores the list's defaults, not an empty set.
  const clearFilters = resetFilters;

  const addButton = (
    <Button onClick={() => list.setCreateOpen(true)}>
      <Plus className="mr-2 h-4 w-4" />
      Add {noun.one}
    </Button>
  );

  const controls = (
    <>
      <FilterPanel
        activeCount={statuses.length}
        summary={filterSummary}
        onReset={clearFilters}
        canReset={hasActiveFilters}
      >
        <FilterRow label="Status">
          <FilterToggleGroup
            options={STATUS_OPTIONS}
            value={statuses}
            onChange={(v) => setFilter("statuses", v)}
          />
        </FilterRow>
      </FilterPanel>

      <DataToolbar
        search={
          <SearchInput
            value={search}
            onValueChange={setSearch}
            placeholder={`Search ${noun.many}…`}
            className="flex-1 min-w-[200px] max-w-sm"
          />
        }
        actions={
          <>
            <ViewToggle value={list.view} onChange={list.setView} />
            {canCreate && addButton}
          </>
        }
      />
    </>
  );

  const createDialog = canCreate
    ? renderCreateDialog?.({ open: list.createOpen, onOpenChange: list.setCreateOpen })
    : null;

  if (isError) {
    return (
      <div className="space-y-4">
        {controls}
        <ErrorState
          message={error?.message ?? `Failed to load ${noun.many}`}
          onRetry={() => refetch()}
        />
        {createDialog}
      </div>
    );
  }

  const emptyState = (
    <EmptyState
      icon={icon}
      title={`No ${noun.many} found`}
      description={
        hasActiveFilters
          ? `No ${noun.many} match your search and filters.`
          : canCreate
            ? `Add your first ${noun.one} to get started.`
            : `There are no ${noun.many} to show.`
      }
      action={
        hasActiveFilters ? (
          <Button variant="outline" onClick={clearFilters}>
            Clear filters
          </Button>
        ) : canCreate ? (
          addButton
        ) : undefined
      }
    />
  );

  return (
    <div className="space-y-4">
      {controls}

      {list.view === "card" ? (
        renderCards({ items: rows, loading: isLoading, empty: emptyState })
      ) : (
        <DataTable
          headers={headers}
          loading={isLoading}
          sort={sort}
          onSort={onSort}
          empty={emptyState}
        >
          {rows.map((item) => renderRow(item))}
        </DataTable>
      )}

      <CursorPagination
        {...pagination(data)}
        limit={LIMIT}
        loading={isLoading}
        itemLabel={noun.many}
      />

      {createDialog}
    </div>
  );
}
